import { type RefObject, useEffect, useRef } from 'react';

interface UseIsInViewportProps {
	onInViewport?: () => void; // Make this required when cleaning up navx-4866-migrate-messages-from-nav-error-boundary
	ref: RefObject<HTMLElement>;
	rootMargin?: string;
}

// When navx-4866-migrate-messages-from-nav-error-boundary is cleaned-up, change this to a void function
// because consumers will be notified via onInViewport of changes.
export function useInViewport({
	ref,
	rootMargin = '0px 0px 200px 0px',
	onInViewport,
}: UseIsInViewportProps): { isInViewport: boolean } {
	// Use a ref instead of state to track viewport visibility.
	// This avoids hydration mismatches caused by IntersectionObserver firing immediately
	// after mount and calling a useState setter, which triggers a re-render.
	// The ref is exposed via a getter for consumers that only need the current value.
	const isInViewportRef = useRef(false);

	useEffect(() => {
		if (!ref.current) {
			isInViewportRef.current = false;
			return;
		}

		const observer = new IntersectionObserver(
			([entry]: IntersectionObserverEntry[]) => {
				isInViewportRef.current = entry.isIntersecting;
				// Call the callback directly in the observer to avoid a chain-of-computation
				// anti-pattern (effect → state/ref → effect).
				if (entry.isIntersecting && onInViewport) {
					onInViewport();
				}
			},
			{ rootMargin },
		);

		observer.observe(ref.current);

		return () => observer.disconnect();
	}, [rootMargin, ref, onInViewport]);

	return { isInViewport: isInViewportRef.current };
}
